import React, { useState, useEffect, useRef } from 'react';

interface Criterion {
  id: number;
  title: string;
  weight: string;
  description: string;
  radarValue: number;
}

interface VisibilityState {
  header: boolean;
  radar: boolean;
  criteria: boolean;
}

const JudgingCriteria = () => {
  const [isVisible, setIsVisible] = useState<VisibilityState>({
    header: false,
    radar: false,
    criteria: false
  });
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const headerRef = useRef<HTMLDivElement>(null);
  const radarRef = useRef<HTMLDivElement>(null);
  const criteriaRef = useRef<HTMLDivElement>(null);

  const criteria: Criterion[] = [
    {
      id: 1,
      title: 'Technical Merit',
      weight: '40%',
      description: 'Proper use of AI tools and platforms, effective application of the tech stack, coding quality, and scalability for sustainable adoption.',
      radarValue: 40
    },
    {
      id: 2,
      title: 'User Experience(UI)',
      weight: '20%',
      description: 'Intuitive interface design and seamless integration of AI features, ensuring accessibility, usability, and engaging interaction for end users.',
      radarValue: 20
    },
    {
      id: 3,
      title: 'Innovation & Creativity',
      weight: '20%',
      description: 'Originality of ideas and disruptive potential, showcasing fresh approaches that leverage AI tools to improve or transform existing processes.',
      radarValue: 20
    },
    {
      id: 4,
      title: 'Impact & Alignment',
      weight: '20%',
      description: 'Clear alignment with the chosen theme and tangible community benefit, demonstrating meaningful contributions through responsible AI solutions.',
      radarValue: 20
    }
  ];

  useEffect(() => {
    const observerOptions: IntersectionObserverInit = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observerCallback: IntersectionObserverCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const targetName = (entry.target as HTMLElement).dataset.animate;
          if (targetName && targetName in isVisible) {
            setIsVisible(prev => ({ ...prev, [targetName as keyof VisibilityState]: true }));
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    if (headerRef.current) observer.observe(headerRef.current);
    if (radarRef.current) observer.observe(radarRef.current);
    if (criteriaRef.current) observer.observe(criteriaRef.current);

    return () => observer.disconnect();
  }, [isVisible]);

  return (
    <div className="min-h-screen bg-black text-white py-16 px-6 overflow-hidden relative">
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-2 h-2 bg-red-600 rounded-full animate-pulse"></div>
        <div className="absolute top-40 right-20 w-1 h-1 bg-red-400 rounded-full animate-ping"></div>
        <div className="absolute bottom-40 left-1/4 w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></div>
        <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-red-300 rounded-full animate-ping"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div 
          ref={headerRef}
          data-animate="header"
          className={`text-center mb-16 transition-all duration-1000 ${
            isVisible.header 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 -translate-y-10'
          }`}
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-4 relative inline-block">
            Judging{' '}
            <span className="text-red-600 relative">
              Criteria
              <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-600 to-transparent animate-pulse"></span>
            </span>
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto mt-6 animate-fade-in">
            Each radar chart visualizes how heavily each criterion influences your final score for that track.
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-gradient-to-br from-gray-900/80 to-black/80 border border-gray-800 rounded-3xl p-8 md:p-12 shadow-2xl backdrop-blur-sm relative overflow-hidden">
          {/* Animated corner accents */}
          <div className="absolute top-0 left-0 w-20 h-20 border-t-2 border-l-2 border-red-600/30 rounded-tl-3xl"></div>
          <div className="absolute bottom-0 right-0 w-20 h-20 border-b-2 border-r-2 border-red-600/30 rounded-br-3xl"></div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Side - Radar Chart */}
            <div 
              ref={radarRef}
              data-animate="radar"
              className={`flex flex-col items-center transition-all duration-1000 delay-300 ${
                isVisible.radar 
                  ? 'opacity-100 translate-x-0 rotate-0' 
                  : 'opacity-0 -translate-x-20 -rotate-12'
              }`}
            >
              <div className="relative">
                {/* Multiple glow layers */}
                <div className="absolute inset-0 bg-red-600/20 blur-3xl rounded-full animate-pulse"></div>
                <div className="absolute inset-0 bg-red-500/10 blur-2xl rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
                
                <svg viewBox="0 0 400 400" className="w-full max-w-md relative z-10">
                  <defs>
                    <radialGradient id="radarGradient">
                      <stop offset="0%" stopColor="#dc2626" stopOpacity="0.5"/>
                      <stop offset="50%" stopColor="#dc2626" stopOpacity="0.2"/>
                      <stop offset="100%" stopColor="#dc2626" stopOpacity="0.05"/>
                    </radialGradient>
                    <filter id="glow">
                      <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                    <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#dc2626"/>
                      <stop offset="100%" stopColor="#ef4444"/>
                    </linearGradient>
                  </defs>

                  {/* Grid circles with animation */}
                  <circle cx="200" cy="200" r="150" fill="none" stroke="#374151" strokeWidth="1" opacity="0.3">
                    <animate attributeName="opacity" values="0.2;0.4;0.2" dur="3s" repeatCount="indefinite"/>
                  </circle>
                  <circle cx="200" cy="200" r="120" fill="none" stroke="#374151" strokeWidth="1" opacity="0.3">
                    <animate attributeName="opacity" values="0.3;0.5;0.3" dur="2.5s" repeatCount="indefinite"/>
                  </circle>
                  <circle cx="200" cy="200" r="90" fill="none" stroke="#374151" strokeWidth="1" opacity="0.3">
                    <animate attributeName="opacity" values="0.2;0.4;0.2" dur="2s" repeatCount="indefinite"/>
                  </circle>
                  <circle cx="200" cy="200" r="60" fill="none" stroke="#374151" strokeWidth="1" opacity="0.3">
                    <animate attributeName="opacity" values="0.3;0.5;0.3" dur="1.5s" repeatCount="indefinite"/>
                  </circle>
                  <circle cx="200" cy="200" r="30" fill="none" stroke="#374151" strokeWidth="1" opacity="0.3">
                    <animate attributeName="opacity" values="0.2;0.4;0.2" dur="1s" repeatCount="indefinite"/>
                  </circle>

                  {/* Axis lines */}
                  <line x1="200" y1="50" x2="200" y2="350" stroke="#374151" strokeWidth="1" opacity="0.3"/>
                  <line x1="50" y1="200" x2="350" y2="200" stroke="#374151" strokeWidth="1" opacity="0.3"/>

                  {/* Data polygon with gradient */}
                  <polygon
                    points="200,80 290,200 200,260 110,200"
                    fill="url(#radarGradient)"
                    stroke="url(#lineGradient)"
                    strokeWidth="3"
                    filter="url(#glow)"
                    className="transition-all duration-500"
                  >
                    <animate attributeName="opacity" values="0.8;1;0.8" dur="2s" repeatCount="indefinite"/>
                  </polygon>

                  {/* Data points with enhanced glow */}
                  <circle cx="200" cy="80" r="7" fill="#dc2626" filter="url(#glow)">
                    <animate attributeName="r" values="6;8;6" dur="1.5s" repeatCount="indefinite"/>
                  </circle>
                  <circle cx="290" cy="200" r="7" fill="#dc2626" filter="url(#glow)">
                    <animate attributeName="r" values="6;8;6" dur="1.5s" repeatCount="indefinite" begin="0.3s"/>
                  </circle>
                  <circle cx="200" cy="260" r="7" fill="#dc2626" filter="url(#glow)">
                    <animate attributeName="r" values="6;8;6" dur="1.5s" repeatCount="indefinite" begin="0.6s"/>
                  </circle>
                  <circle cx="110" cy="200" r="7" fill="#dc2626" filter="url(#glow)">
                    <animate attributeName="r" values="6;8;6" dur="1.5s" repeatCount="indefinite" begin="0.9s"/>
                  </circle>

                  {/* Labels */}
                  <text x="200" y="40" textAnchor="middle" fill="#9ca3af" fontSize="13" fontWeight="600">
                    Technical Merit
                  </text>
                  <text x="330" y="205" textAnchor="start" fill="#9ca3af" fontSize="13" fontWeight="600">
                    User Experience(UI)
                  </text>
                  <text x="200" y="390" textAnchor="middle" fill="#9ca3af" fontSize="13" fontWeight="600">
                    Innovation & Creativity
                  </text>
                  <text x="70" y="205" textAnchor="end" fill="#9ca3af" fontSize="13" fontWeight="600">
                    Impact & Alignment
                  </text>
                </svg>
              </div>

              <p className="text-gray-500 text-sm mt-8 text-center max-w-xs animate-fade-in">
                The larger the area toward a point, the more that criterion influences your score.
              </p>
            </div>

            {/* Right Side - Criteria List */}
            <div 
              ref={criteriaRef}
              data-animate="criteria"
              className="space-y-6"
            >
              {criteria.map((criterion, index) => (
                <div
                  key={criterion.id}
                  onMouseEnter={() => setHoveredCard(criterion.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  className={`bg-gradient-to-br from-gray-900/50 to-gray-900/30 border border-gray-800 rounded-xl p-6 hover:border-red-900/70 hover:bg-gray-900/70 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-red-900/30 relative overflow-hidden ${
                    isVisible.criteria 
                      ? 'opacity-100 translate-x-0' 
                      : 'opacity-0 translate-x-20'
                  }`}
                  style={{
                    transitionDelay: isVisible.criteria ? `${500 + index * 150}ms` : '0ms'
                  }}
                >
                  {/* Hover glow effect */}
                  {hoveredCard === criterion.id && (
                    <div className="absolute inset-0 bg-gradient-to-r from-red-600/5 via-red-600/10 to-red-600/5 animate-pulse pointer-events-none"></div>
                  )}
                  
                  {/* Left accent line */}
                  <div className={`absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-red-600 to-transparent transition-all duration-500 ${
                    hoveredCard === criterion.id ? 'opacity-100' : 'opacity-0'
                  }`}></div>

                  <div className="flex items-start gap-4 relative z-10">
                    <div className="flex-shrink-0">
                      <div className={`w-14 h-14 rounded-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center font-bold text-xl shadow-lg transition-all duration-300 ${
                        hoveredCard === criterion.id 
                          ? 'shadow-red-600/70 scale-110 rotate-12' 
                          : 'shadow-red-600/50'
                      }`}>
                        <span className="relative z-10">{criterion.id}</span>
                        {/* Spinning ring on hover */}
                        {hoveredCard === criterion.id && (
                          <div className="absolute inset-0 border-2 border-red-400 rounded-full animate-spin" style={{ animationDuration: '3s' }}></div>
                        )}
                      </div>
                    </div>
                    <div className="flex-grow">
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <h3 className="text-xl font-bold text-white transition-colors duration-300">
                          {criterion.title}
                        </h3>
                        <span className={`bg-red-900/30 text-red-400 px-4 py-1 rounded-full text-sm font-semibold border border-red-800/50 transition-all duration-300 ${
                          hoveredCard === criterion.id ? 'scale-110 bg-red-900/50' : ''
                        }`}>
                          {criterion.weight}
                        </span>
                      </div>
                      <p className="text-gray-400 leading-relaxed">
                        {criterion.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style >{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fade-in 1s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default JudgingCriteria;